import PropTypes from 'prop-types';
import React from 'react';

import XYChart from './XYChart.jsx';
import {ChartBaseContent} from './Chart.jsx';

import {scaleBand} from 'd3-scale';

import {looseParseTime, timeToSpan} from '../lib/TimeUtils';

const BAR_MIN_HEIGHT = 2;
const BAR_MIN_WIDTH = 2;
const BAR_MIN_MARGIN = 1;

const NON_HILIGHT_OPACITY = 0.3;
const SEMI_HILIGHT_OPACITY = 0.6;
const HILIGHT_OPACITY = 1;


class StackedBars extends ChartBaseContent {
    static displayName = 'StackedBars';

    doDraw() {
        super.doDraw();

        let {yScale, xScale, colors, data, type} = this.props;

        let {width, height} = this.getSize();

        let yKey = data.keys("y"),
            xKey = data.keys("x"),
            groupBy = data.keys("group"),
            items = data.values().reverse();


        let ctx = this.getCtx();

        if (items.length === 0) {
            return;
        }

        ctx.save();
        ctx.beginPath();

        ctx.globalAlpha = HILIGHT_OPACITY;


        let getY = value => yScale(Math.max(yScale.invert(1 - BAR_MIN_HEIGHT / height), value)) * height;
        let getX = name => {
            if (type == "time") {
                return xScale(looseParseTime(name)) * width + 1;
            } else {
                return xScale(name) * width;
            }
        };

        let barWidth = Math.max((()=> {
            if (typeof xScale.bandwidth == "function") {
                return xScale.bandwidth() * width;
            } else if (type == "time") {
                let {start, end} = timeToSpan(items[0][xKey]);
                return (xScale(end) - xScale(start)) * width - BAR_MIN_MARGIN * 2;
            } else {
                return Math.round(width / xScale.domain().length) * 0.9;
            }
        })(), BAR_MIN_WIDTH);

        let grouped = {};

        for (let item of items) {
            let xPos = getX(item[xKey]);
            if (!grouped[xPos]) {
                grouped[xPos] = {sum: 0, items: []};
            }

            let value = parseFloat(item[yKey]),
                h = height - getY(value),
                y0 = getY(grouped[xPos].sum + value);

            grouped[xPos].items.push({item, y0, y1: y0 + h});
            grouped[xPos].sum += value;

            ctx.fillStyle = colors.get(item[groupBy]);
            ctx.fillRect(xPos, y0, barWidth, h);
            ctx.fill();
        }

        ctx.restore();

        this._drawHoveredItems = (ctx, hoveredItem) => {
            for (let xPos of Object.keys(grouped)){
                for (let cell of grouped[xPos].items) {
                    let {item, y0, y1} = cell;
                    if (hoveredItem[groupBy] == item[groupBy]) {
                        if (!hoveredItem[xKey] || hoveredItem[xKey] == item[xKey]) {
                            ctx.globalAlpha = HILIGHT_OPACITY;
                        } else {
                            ctx.globalAlpha = SEMI_HILIGHT_OPACITY;
                        }
                    } else {
                        continue;
                    }

                    let h = y1 - y0;

                    if (h < 10) {
                        y0 -= 1;
                        h += 2;
                    }

                    ctx.fillStyle = colors.get(item[groupBy]);
                    ctx.fillRect(xPos, y0, barWidth, h);
                    ctx.fill();
                }
            }
        };

        this._reverseLookup = (x, y) => {
            let xIsInside = (posx, x0, x1) => x0 <= posx && x1 >= posx;
            let yIsInside = (posy, y0, y1) => y0 <= posy && y1 >= posy;

            for (let key of Object.keys(grouped)) {
                let x0 = parseFloat(key) - BAR_MIN_MARGIN;
                let x1 = x0 + barWidth + BAR_MIN_MARGIN * 2;
                if (xIsInside(x, x0, x1)) {
                    for (let {y0, y1, item} of grouped[key].items) {
                        if (yIsInside(y, y0, y1)) {
                            return {
                                item,
                                isValid: (x, y) => xIsInside(x, x0, x1) && yIsInside(y, y0, y1)
                            };
                        }
                    }
                }
            }

            return null;
        };
    }

}


class GroupedBars extends ChartBaseContent {
    static displayName = 'GroupedBars';

    doDraw() {
        super.doDraw();

        let {yScale, xScale, colors, data, groups, type, hoveredItem} = this.props;

        let {width, height} = this.getSize();

        let yKey = data.keys("y"),
            xKey = data.keys("x"),
            groupBy = data.keys("group"),
            items = data.values();


        let ctx = this.getCtx();


        if (items.length === 0) {
            return;
        }

        ctx.save();
        ctx.beginPath();
        ctx.globalAlpha = HILIGHT_OPACITY;

        let getY = value => yScale(Math.max(yScale.invert(1 - BAR_MIN_HEIGHT / height), value)) * height;
        let getX = name => {
            if (type == "time") {
                return xScale(looseParseTime(name)) * width + BAR_MIN_MARGIN;
            } else {
                return xScale(name) * width;
            }
        };

        let groupWidth = (()=> {
            if (typeof xScale.bandwidth == "function") {
                return xScale.bandwidth() * width;
            } else if (type == "time") {
                let {start, end} = timeToSpan(items[0][xKey]);
                return (xScale(end) - xScale(start)) * width - BAR_MIN_MARGIN * 2;
            } else {
                return Math.round(width / xScale.domain().length) * 0.9;
            }
        })();

        let x2 = scaleBand()
            .domain(groups.map(g => g.name))
            .range([0, groupWidth]);

        let bars = [];

        let barWidth = Math.max(x2.bandwidth(), BAR_MIN_WIDTH);

        for (let item of items) {
            let xPos = Math.round(getX(item[xKey]) + x2(item[groupBy]));

            let value = getY(parseFloat(item[yKey])),
                h = height - value,
                y0 = value;

            bars.push({item, x: xPos, y0});


            if (hoveredItem) {
                if (hoveredItem[groupBy] == item[groupBy]) {
                    if (!hoveredItem[xKey] || hoveredItem[xKey] == item[xKey]) {
                        ctx.globalAlpha = HILIGHT_OPACITY;
                    } else {
                        ctx.globalAlpha = SEMI_HILIGHT_OPACITY;
                    }

                    if (h < 10) {
                        y0 -= 1;
                        h += 2;
                    }
                } else {
                    ctx.globalAlpha = NON_HILIGHT_OPACITY;
                }
            }

            ctx.fillStyle = colors.get(item[groupBy]);
            ctx.fillRect(xPos, y0, barWidth, h);
            // console.log(getX(name), y0, barWidth, h);
            ctx.fill();
        }

        ctx.restore();

        this._reverseLookup = (x, y) => {
            let xIsInside = (posx, x0, x1) => x0 <= posx && x1 >= posx;
            let yIsInside = (posy, y0, y1) => y0 <= posy && y1 >= posy;

            for (let bar of bars) {
                let x0 = bar.x,
                    x1 = x0 + barWidth,
                    y0 = bar.y0,
                    y1 = height;

                if (xIsInside(x, x0, x1)) {
                    if (yIsInside(y, y0, y1)) {
                        return {
                            item: bar.item,
                            isValid: (x, y) => xIsInside(x, x0, x1) && yIsInside(y, y0, y1)
                        };
                    } else {
                        return null;
                    }
                }
            }

            return null;
        };
    }
}


const BarChartSelector = props => {
    const {stack, groups} = props;
    const Bars = stack || groups.length == 0 ? StackedBars : GroupedBars;
    return <Bars {...props} />;
};

BarChartSelector.propTypes = {
    stack: PropTypes.bool,
    groups: PropTypes.array
};

export default XYChart(BarChartSelector, {ordinalScale: () => scaleBand().padding(0.05), defaultStack: true});


// WEBPACK FOOTER //
// ./src/kpi/charts/BarChart.jsx